// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import locationTypeTranslations from 'infras/i18n/locales/location_type.json';
import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  fontSize,
  spacing,
  mediaMax,
  fontWeight,
} from 'presentations/views/styles';
import {
  formatOrderItemDate,
  formatPrice,
} from 'presentations/views/helpers/formatter';
import { DateTime } from 'infras/datetime';
import { EventPaymentItem } from 'presentations/api/schema/event_payment';
import EventPayment from 'domains/models/event_payment';
import { TaxInclusive } from 'domains/models/tax_rate/tax_inclusive';
import { PaymentMethod } from 'domains/models/event_payment/payment_method';
import { white, gray04 } from 'presentations/views/styles/color';
interface Props {
  eventPayment: EventPaymentItem;
  startDatetime: DateTime;
  endDatetime: DateTime;
  timeZone: string;
  isShowSchedule: boolean;
  isShowPaymentMethodOnly: boolean;
  isNoBorder: boolean;
  footer?: ReactNode;
}

export default function Component(props: Props) {
  const t = translateInComponent([translations, locationTypeTranslations]);
  const currency = props.eventPayment.currency;
  const eventPayment = new EventPayment(props.eventPayment);

  return (
    <Container isNoBorder={props.isNoBorder} className="paymentDetail">
      {!props.isShowPaymentMethodOnly && (
        <>
          {props.isShowSchedule && (
            <OrderItem isFirstItem={props.isShowSchedule} boldSize={2}>
              <OrderItemName isBold={true}>{t('date')}</OrderItemName>
              <OrderItemValue>
                {formatOrderItemDate(
                  props.startDatetime,
                  props.endDatetime,
                  props.timeZone,
                )}
              </OrderItemValue>
            </OrderItem>
          )}

          <OrderItem isFirstItem={!props.isShowSchedule}>
            <OrderItemName isBold={true}>{t('charge')} </OrderItemName>
          </OrderItem>

          {props.eventPayment.items.map((item, index) => (
            <OrderSubItem
              isLastItem={index === props.eventPayment.items.length - 1}
              key={item.serviceId}
            >
              <OrderItemName>
                {item.name}
                {item.taxInclusive === TaxInclusive.Inclusive &&
                  t('TaxInclusive')}
              </OrderItemName>
              <OrderItemValue>
                {formatPrice(item.unitAmount, currency)}
              </OrderItemValue>
            </OrderSubItem>
          ))}
          <OrderSubItem>
            <OrderItemName>{t('subTotalAmount')}</OrderItemName>
            <OrderItemValue>
              {formatPrice(eventPayment.calcSubTotalAmount(), currency!)}
            </OrderItemValue>
          </OrderSubItem>
          {eventPayment.calcTotalInclusiveTaxAmount() > 0 && (
            <OrderSubItem
              isLastItem={eventPayment.calcTotalExclusiveTaxAmount() === 0}
            >
              <OrderItemName>{t(`totalTaxAmount 2`)}</OrderItemName>
              <OrderItemValue>
                (
                {formatPrice(
                  eventPayment.calcTotalInclusiveTaxAmount(),
                  currency!,
                )}
                )
              </OrderItemValue>
            </OrderSubItem>
          )}
          {eventPayment.calcTotalExclusiveTaxAmount() > 0 && (
            <OrderSubItem isLastItem={true}>
              <OrderItemName>{t(`totalTaxAmount 1`)}</OrderItemName>
              <OrderItemValue>
                {formatPrice(
                  eventPayment.calcTotalExclusiveTaxAmount(),
                  currency!,
                )}
              </OrderItemValue>
            </OrderSubItem>
          )}
          <OrderItem boldSize={2}>
            <OrderItemName isBold={true}>{t('totalAmount')}</OrderItemName>
            <OrderItemValue isBold={true}>
              {formatPrice(eventPayment.totalAmount, currency!)}
            </OrderItemValue>
          </OrderItem>
        </>
      )}
      {props.eventPayment.paymentMethod !== PaymentMethod.None && (
        <OrderItem isFirstItem={props.isShowPaymentMethodOnly} boldSize={2}>
          <OrderItemName isBold={true}>{t('PaymentMethod')}</OrderItemName>
          <OrderItemValue>
            {t(`PaymentMethod ${props.eventPayment.paymentMethod}`)}
          </OrderItemValue>
        </OrderItem>
      )}
      {props.footer}
    </Container>
  );
}

const Container = styled.div<{
  isNoBorder: boolean;
}>`
  position: relative;
  align-items: center;
  margin-bottom: ${spacing(16)};
  font-size: ${fontSize(14)};
  border-radius: 6px;
  padding: ${spacing(24)};

  ${(props) =>
    css`
      border: ${props.isNoBorder ? 0 : 1}px solid ${gray04(props)};
    `}

  &:last-of-type {
    margin-bottom: 0;
  }
`;
const OrderItem = styled.div<{
  isFirstItem?: boolean;
  boldSize?: number;
}>`
  background-color: ${white};
  padding: ${spacing(16)} 0;
  ${(props) =>
    css`
      border-bottom: ${props.boldSize !== undefined ? props.boldSize : 1}px
        solid ${gray04(props)};
    `}

  ${mediaMax.tablet} {
    padding: ${spacing(16)} 0;
  }

  ${mediaMax.tablet} {
    padding-left: 0;
    margin-right: 0;
  }

  ${(props) =>
    props.isFirstItem &&
    css`
      border-top: ${props.boldSize !== undefined ? props.boldSize : 1}px solid
        ${gray04(props)};
    `}
`;
const OrderSubItem = styled.div<{
  isLastItem?: boolean;
}>`
  background-color: ${white};
  padding: ${spacing(6)} 0;
  ${mediaMax.tablet} {
    padding: ${spacing(6)} 0;
  }

  ${mediaMax.tablet} {
    padding-left: 0;
    margin-right: 0;
  }

  ${(props) =>
    props.isLastItem &&
    css`
      border-bottom: 1px solid ${gray04(props)};
    `}
`;
const OrderItemName = styled.div<{
  isBold?: boolean;
}>`
  display: inline-block;
  font-size: ${fontSize(14)};
  max-width: 50%;

  ${({ isBold }) =>
    isBold &&
    css`
      font-weight: ${fontWeight('bold')};
    `}
`;
const OrderItemValue = styled.span<{
  isBold?: boolean;
}>`
  float: right;
  font-size: ${fontSize(14)};

  ${({ isBold }) =>
    isBold &&
    css`
      font-weight: ${fontWeight('bold')};
    `}
`;
